/**
 * Mood evidence engine — the math behind "here's what movement actually does
 * for you."
 *
 * WHY WITHIN-SESSION DELTA IS THE HEADLINE METRIC:
 * The obvious stat ("your mood is higher on days you train") is confounded —
 * people train *because* they already feel good, so that number partly measures
 * reverse causation. Comparing mood before vs. after the same session, on the
 * same day, controls for that: the only thing that changed is the workout.
 * It's a weaker claim statistically than a trial, but it's honest, and it's far
 * more persuasive because it's the user's own data.
 *
 * Everything here is pure so it can be tested without a database.
 */

/** Pair pre/post check-ins for the same session. */
export function pairSessions(checkins) {
  const byWorkout = new Map();
  for (const c of checkins || []) {
    if (!c.workout_id) continue;
    if (c.context !== "pre_session" && c.context !== "post_session") continue;
    if (!byWorkout.has(c.workout_id)) byWorkout.set(c.workout_id, {});
    const slot = byWorkout.get(c.workout_id);
    if (c.context === "pre_session") slot.pre = c;
    else slot.post = c;
  }

  const pairs = [];
  for (const [workoutId, { pre, post }] of byWorkout) {
    if (!pre || !post) continue;
    pairs.push({
      workoutId,
      date: post.date || pre.date,
      before: pre.mood,
      after: post.mood,
      delta: post.mood - pre.mood,
    });
  }
  return pairs.sort((a, b) => (a.date < b.date ? -1 : 1));
}

/**
 * Fall back to the daily check-in as the "before" when a session has a post
 * check-in but no explicit pre — as long as the daily came earlier the same day.
 * This is what keeps the loop low-friction: users don't have to check in twice
 * to get evidence.
 */
export function pairSessionsWithDailyFallback(checkins) {
  const explicit = pairSessions(checkins);
  const covered = new Set(explicit.map((p) => p.workoutId));

  const dailyByDate = new Map();
  for (const c of checkins || []) {
    if (c.context !== "daily") continue;
    const prev = dailyByDate.get(c.date);
    // earliest daily check-in of that day is the truest baseline
    if (!prev || new Date(c.created_at) < new Date(prev.created_at)) {
      dailyByDate.set(c.date, c);
    }
  }

  const inferred = [];
  for (const c of checkins || []) {
    if (c.context !== "post_session" || !c.workout_id) continue;
    if (covered.has(c.workout_id)) continue;
    const daily = dailyByDate.get(c.date);
    if (!daily) continue;
    if (new Date(daily.created_at) > new Date(c.created_at)) continue; // daily came after
    inferred.push({
      workoutId: c.workout_id,
      date: c.date,
      before: daily.mood,
      after: c.mood,
      delta: c.mood - daily.mood,
      inferred: true,
    });
  }

  return [...explicit, ...inferred].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Minimum paired sessions before we're willing to claim anything. */
export const MIN_PAIRS_FOR_EVIDENCE = 3;

/**
 * Build the Evidence Card payload.
 * Returns null when there isn't enough data to say something honest.
 */
export function computeMoodEvidence(checkins) {
  const pairs = pairSessionsWithDailyFallback(checkins);
  if (pairs.length < MIN_PAIRS_FOR_EVIDENCE) {
    return {
      ready: false,
      sessionsPaired: pairs.length,
      sessionsNeeded: MIN_PAIRS_FOR_EVIDENCE - pairs.length,
    };
  }

  const deltas = pairs.map((p) => p.delta);
  const avgDelta = deltas.reduce((a, b) => a + b, 0) / deltas.length;
  const improved = deltas.filter((d) => d > 0).length;
  const unchanged = deltas.filter((d) => d === 0).length;

  // The most motivating cut: sessions started on a bad day (mood <= 2).
  //
  // It obeys the same three-pair floor as the headline. It used to be surfaced
  // at two — "on the 2 days you started low, moving lifted you +2.5" — which is
  // the product's one hard rule broken by its most quotable number, on the
  // screen a clinician reads aloud to the patient it describes.
  const roughDays = pairs.filter((p) => p.before <= 2);
  const roughDayAvg = roughDays.length
    ? roughDays.reduce((a, p) => a + p.delta, 0) / roughDays.length
    : null;

  return {
    ready: true,
    sessions: pairs.length,
    avgDelta: Math.round(avgDelta * 10) / 10,
    improved,
    unchanged,
    worsened: deltas.length - improved - unchanged,
    roughDaySessions: roughDays.length,
    roughDayAvgDelta:
      roughDays.length < MIN_PAIRS_FOR_EVIDENCE || roughDayAvg === null
        ? null
        : Math.round(roughDayAvg * 10) / 10,
    roughDayReady: roughDays.length >= MIN_PAIRS_FOR_EVIDENCE,
    best: pairs.reduce((a, b) => (b.delta > a.delta ? b : a)),
    pairs,
  };
}

/**
 * Secondary, clearly-labeled stat: average daily mood on days with a workout
 * vs. days without. Correlational — the UI must not imply causation.
 */
export function computeDayComparison(checkins, workoutDates) {
  const trainDates = new Set(workoutDates || []);
  const daily = (checkins || []).filter((c) => c.context === "daily");
  const on = daily.filter((c) => trainDates.has(c.date)).map((c) => c.mood);
  const off = daily.filter((c) => !trainDates.has(c.date)).map((c) => c.mood);
  if (on.length < 3 || off.length < 3) return null;
  const avg = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
  return {
    trainingDays: Math.round(avg(on) * 10) / 10,
    restDays: Math.round(avg(off) * 10) / 10,
    trainingDayCount: on.length,
    restDayCount: off.length,
  };
}

export const MOOD_LABELS = ["Awful", "Low", "OK", "Good", "Great"];
export const ENERGY_LABELS = ["Drained", "Low", "Steady", "Fresh", "Wired"];

/**
 * Map mood+energy to a movement prescription archetype. This is the inversion
 * that matters: the objective is adherence, not performance. On the worst days
 * the ask must be small enough to actually happen.
 */
export function prescriptionFor(mood, energy) {
  if (mood <= 2 && energy <= 2) {
    return { key: "gentle", minutes: 10, label: "A 10-minute walk", why: "Lowest bar that still counts. Starting is the whole goal today." };
  }
  if (mood <= 2 && energy >= 4) {
    return { key: "discharge", minutes: 25, label: "Hard cardio or intervals", why: "Restless and low — burning off the agitation tends to help most." };
  }
  if (mood <= 3 && energy <= 3) {
    return { key: "easy", minutes: 20, label: "Easy movement — walk or light lift", why: "Keep it light. Consistency beats intensity when you're flat." };
  }
  if (mood >= 4 && energy >= 4) {
    return { key: "push", minutes: 45, label: "Full session — push it", why: "You've got capacity today. Good day to bank a hard one." };
  }
  return { key: "standard", minutes: 30, label: "Your regular session", why: "Steady state — normal training works well here." };
}

/**
 * Merge server check-ins into the local set without creating duplicates.
 *
 * Records written on this device carry a client-generated `id`; once synced we
 * remember the server's id as `remoteId`. A record is a duplicate if we already
 * know its remote id, or if the ids match outright.
 *
 * Pure so it's testable without any environment or network.
 */
export function mergeCheckins(local, remote) {
  const localList = local || [];
  const knownRemoteIds = new Set(localList.map((r) => r.remoteId).filter(Boolean));
  const localIds = new Set(localList.map((r) => r.id));
  const additions = (remote || []).filter(
    (r) => !knownRemoteIds.has(r.id) && !localIds.has(r.id)
  );
  return [...localList, ...additions].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
}
